'use client'
import { Box, Center, Flex, Loader, ScrollArea } from '@mantine/core'
import { useMemo, useState } from 'react'

import { useCoinContext } from '@/app/[locale]/price/[slug]/_contexts/CoinContext'
import { SectionErrorBoundary } from '@/components/error/boundary'
import { useSsrContext } from '@/contexts/ssr'
import { useCoinPricesBySlug } from '@/services/coin/prices'

import BasicLineChart from './components/BasicLineChart'
import ChartInfoTitle from './components/ChartInfoTitle'
import CoinTitle from './components/CoinTitle'
import SettingButton from './components/SettingButton'
import TimeRangeItem from './components/TimeRangeItem'
import TradingViewChart from './components/TradingViewChart'
import { useTitle } from './hooks'
import { BTC_SLUG, CHART_HEIGHT, SettingType, TIME_RANGE_OPTIONS } from './utils'

interface CoinLineChartProps {
  customHeader?: React.ReactNode
}

function CoinLineChart({ customHeader }: CoinLineChartProps) {
  const { coin } = useCoinContext()
  const { slug } = coin
  const isBtc = slug === BTC_SLUG
  const [curTimeRange, setCurTimeRange] = useState('24H')
  const [settingType, setSettingType] = useState<SettingType | null>(null)
  const queryParams = useMemo(() => {
    const targetOption = TIME_RANGE_OPTIONS.find(option => option.value === curTimeRange)
    return targetOption!.getQueryParams()
  }, [curTimeRange])
  const { selectedCurrency } = useSsrContext()
  const { data: currentCoinPrices, isFetching: isFetchingCurrentCoinPrices } = useCoinPricesBySlug({
    slug,
    ...queryParams,
    convert: selectedCurrency,
  })
  const { data: btcCoinPrices, isFetching: isFetchingBtcCoinPrices } = useCoinPricesBySlug({
    slug,
    ...queryParams,
    convert: 'btc',
  })

  const btcPricesVisible = settingType === SettingType.BTC_PRICE
  const { setMousePointData, coinPrice, coinPriceChange, btcPrice, isMouseInChart } = useTitle({
    btcPricesVisible,
    currentCoinPrices,
  })
  return (
    <Box flex={1}>
      <CoinTitle
        currentCoinPrices={currentCoinPrices}
        curTimeRange={curTimeRange}
        slug={slug}
        customHeader={customHeader}
      />
      <ChartInfoTitle
        btcPrice={btcPrice}
        coinPrice={coinPrice}
        coinPercentChange={coinPriceChange}
        isMouseInChart={isMouseInChart}
      />
      {settingType === SettingType.TRADING_VIEW ? (
        <TradingViewChart />
      ) : isFetchingCurrentCoinPrices || isFetchingBtcCoinPrices ? (
        <Center h={CHART_HEIGHT}>
          <Loader />
        </Center>
      ) : (
        <BasicLineChart
          key={curTimeRange}
          btcPricesVisible={btcPricesVisible}
          btcCoinPrices={btcCoinPrices}
          currentCoinPrices={currentCoinPrices}
          onShowPriceTooltip={setMousePointData}
        />
      )}

      <Flex align={'center'} h={36}>
        <SettingButton
          settingType={settingType}
          onSettingChange={setSettingType}
          bTCPriceButtonVisible={!isBtc}
        />
        <ScrollArea>
          <Flex>
            {settingType !== SettingType.TRADING_VIEW &&
              TIME_RANGE_OPTIONS.map(item => (
                <TimeRangeItem
                  key={item.value}
                  isActive={curTimeRange === item.value}
                  value={item.value}
                  label={item.label}
                  onClick={setCurTimeRange}
                />
              ))}
          </Flex>
        </ScrollArea>
      </Flex>
    </Box>
  )
}
export default SectionErrorBoundary.with(CoinLineChart)
